import { ValueModel } from '@kts-front/models';
import { Nullable } from '@kts-front/types';
import { AsYouType, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import { action, computed, makeObservable, override } from 'mobx';

import { ClientUniquenessStore } from 'stores/global/ClientUniquenessStore';
import { rootStore } from 'stores/global/RootStore';
import { formatPhoneForBackend } from 'utils/formatPhoneForBackend';
import { validatePhoneNumber } from 'utils/validators/validators';

import { FieldTextStore, FieldTextStoreOptions } from '../FieldTextStore';

import { COUNTRIES, CountryCode, DEFAULT_COUNTRY } from './countries';

export type FieldPhoneStoreOptions = FieldTextStoreOptions & {
  checkUniqueClientStore?: ClientUniquenessStore;
};

export class FieldPhoneStore extends FieldTextStore {
  private readonly _checkUniqueClientStore?: ClientUniquenessStore;
  readonly selectedCountry = new ValueModel<CountryCode>(DEFAULT_COUNTRY);
  readonly search = new ValueModel<string>('');

  constructor(options?: FieldPhoneStoreOptions) {
    super(options);

    this._checkUniqueClientStore = options?.checkUniqueClientStore;

    makeObservable(this, {
      isUnique: computed,
      loadingStageUnique: computed,
      shouldShowFields: computed,
      onSearchChange: action.bound,
      onSearchClear: action.bound,
      filteredCountries: computed,
      serverFormat: override,
      syncValueFromExternal: action.bound,
      handleInputChange: action.bound,
    });
  }

  get isUnique(): Nullable<boolean> {
    return this._checkUniqueClientStore?.isUnique.value ?? null;
  }

  get loadingStageUnique() {
    return this._checkUniqueClientStore?.loadingStageUnique;
  }

  get shouldShowFields(): boolean {
    const isPhoneValid = !validatePhoneNumber()(this.value).value;

    if (!this._checkUniqueClientStore) {
      return isPhoneValid;
    }

    if (!rootStore.appConfigStore.logic.checkUniquePhone) {
      return isPhoneValid;
    }

    return isPhoneValid && this.isUnique === true;
  }

  get filteredCountries(): CountryCode[] {
    const searchLower = this.search.value.toLowerCase().trim();

    if (!searchLower) {
      return COUNTRIES;
    }

    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(searchLower) || c.dialCode.includes(searchLower));
  }

  onSearchChange(value: string): void {
    this.search.change(value);
  }
  onSearchClear(): void {
    this.search.change('');
  }

  setValue(value: string | null, options?: { noTouch?: boolean }): void {
    super.setValue(value, options);

    if (this._checkUniqueClientStore && rootStore.appConfigStore.logic.checkUniquePhone) {
      this._checkUniqueClientStore.checkUnique(value);
    }
  }

  setUniqueResult(isUnique: boolean | null): void {
    this._checkUniqueClientStore?.setUniqueResult(isUnique);
  }

  get serverFormat(): string | null {
    return formatPhoneForBackend(this.value);
  }

  static extractNationalNumber(digits: string, dialCode: string): string {
    const countryDialCode = dialCode.replace(/\D/g, '');

    return digits.startsWith(countryDialCode) ? digits.slice(countryDialCode.length) : digits;
  }

  static getCountryFromValue(value: string, currentCountry: CountryCode): CountryCode {
    const dialCodeMatch = value.match(/^\+(\d+)/);

    if (!dialCodeMatch) {
      return currentCountry;
    }

    const dialCode = `+${dialCodeMatch[1]}`;
    const country = COUNTRIES.find((c) => c.dialCode === dialCode);

    return country ?? currentCountry;
  }

  syncValueFromExternal(): { formattedValue: string; nationalNumber: string } | null {
    if (!this.value) {
      return null;
    }

    const digits = this.value.replace(/\D/g, '');

    if (!digits) {
      return null;
    }

    const country = FieldPhoneStore.getCountryFromValue(this.value, this.selectedCountry.value);

    if (country.code !== this.selectedCountry.value.code) {
      this.selectedCountry.change(country);
    }

    const nationalNumber = FieldPhoneStore.extractNationalNumber(digits, this.selectedCountry.value.dialCode);
    const fmt = new AsYouType(country.code);
    const formatted = fmt.input(nationalNumber);

    return {
      formattedValue: formatted,
      nationalNumber,
    };
  }

  handleInputChange(
    inputValue: string,
    previousFormattedValue: string,
    previousDigits: string,
  ): { formattedValue: string; digits: string } {
    let digits = inputValue.replace(/\D/g, '');

    const wasDeleting = inputValue.length < previousFormattedValue.length;

    if (wasDeleting && digits.length >= previousDigits.length && digits.length > 0) {
      digits = digits.slice(0, -1);
    }

    const maxDigits = this.getMaxDigits();
    const limitedDigits = digits.slice(0, maxDigits);

    const fmt = new AsYouType(this.selectedCountry.value.code);
    const formatted = fmt.input(limitedDigits);

    this.setValue(`${this.selectedCountry.value.dialCode} ${formatted}`);

    return {
      formattedValue: formatted,
      digits: limitedDigits,
    };
  }

  getMaxDigits(): number {
    const example = getExampleNumber(this.selectedCountry.value.code, examples);

    return example?.nationalNumber.length ?? 15;
  }
}
